import type { GarantieDto } from '@/api/generated';
import { useGaranties, useProject } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { formatShortDate } from '@/lib/format';
import styles from './Warranties.module.css';

// 2026-05-17 — Warranties page cleanup:
//   * DECADE_RULER previously hardcoded "11/2026 → 11/2036". Now derived
//     from the project's endDate (the voorlopige-oplevering signal) via
//     `useProject`. If the project has no end date yet, the ruler shows
//     a clear empty state instead of inventing dates.
//   * ONDERHOUD list ("HVAC SeBau, CV-ketel Vandeput, Voegwerk Heyvaert")
//     and the Opleveringspunten cards were Phase-1 fixture data with no
//     API backing. Both cards removed; reintroduce when matching entities
//     land on the backend.

interface RulerStop {
  label: string;
  date: string;
  start?: boolean;
  now?: boolean;
  end?: boolean;
}

function buildDecadeRuler(opleveringIso: string | null | undefined): RulerStop[] {
  if (!opleveringIso) return [];
  const start = new Date(opleveringIso);
  if (Number.isNaN(start.getTime())) return [];
  const now = Date.now();
  const fmt = (d: Date) => {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${m}/${d.getFullYear()}`;
  };
  const at = (years: number) => {
    const d = new Date(start);
    d.setFullYear(d.getFullYear() + years);
    return d;
  };
  const stops: RulerStop[] = [
    { label: 'oplevering', date: fmt(start), start: true },
    { label: 'jaar 1', date: fmt(at(1)) },
    { label: 'jaar 3', date: fmt(at(3)) },
    { label: 'jaar 5', date: fmt(at(5)) },
    { label: 'jaar 7', date: fmt(at(7)) },
    { label: 'jaar 10 — einde', date: fmt(at(10)), end: true },
  ];
  // Mark the stop the user is closest to as "now" (visual anchor).
  if (now >= start.getTime() && now <= at(10).getTime()) {
    const yearsSince = (now - start.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    let bestIdx = 0;
    let bestDelta = Infinity;
    for (const [i, s] of stops.entries()) {
      const target = i === 0 ? 0 : Number(s.label.match(/\d+/)?.[0] ?? 0);
      const delta = Math.abs(target - yearsSince);
      if (delta < bestDelta) {
        bestDelta = delta;
        bestIdx = i;
      }
    }
    stops[bestIdx].now = true;
  }
  return stops;
}

function durationLabel(starts: string, ends: string): string {
  const start = new Date(starts);
  const end = new Date(ends);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '—';
  const years = Math.round((end.getTime() - start.getTime()) / (365.25 * 86_400_000));
  return `${years} jaar`;
}

function pillKindFor(g: GarantieDto): 'ok' | 'warn' | 'idle' {
  if (g.component.toLowerCase().includes('decennale')) return 'warn';
  if (g.component.toLowerCase().includes('hvac')) return 'idle';
  return 'ok';
}

export function Warranties() {
  const garanties = useGaranties();
  const project = useProject();
  const decadeRuler = buildDecadeRuler(project.data?.endDate);

  return (
    <AppFrame>
      <div className={`row ${styles.headerRow}`}>
        <div>
          <div className="eyebrow">Nazorg · loopt vanaf voorlopige oplevering</div>
          <h1 className={styles.title}>Garanties &amp; onderhoud</h1>
          <p className={styles.subtitle}>
            Termijnen, garantievoorwaarden en onderhoudsschema's — verzameld zodat je in jaar 7 nog
            weet wie je moet bellen.
          </p>
        </div>
      </div>

      <Card className={styles.decade}>
        <div className={`row ${styles.decadeHead}`}>
          <h3>10-jarige aansprakelijkheid</h3>
          <span className={`ml-auto mono ${styles.decadeRef}`}>Wet Breyne · BW art. 1792</span>
        </div>
        <p className={styles.decadeBody}>
          Start op datum van voorlopige oplevering. Bouwheer kan tot 10 jaar later schade aan
          dragende elementen claimen.
        </p>

        {decadeRuler.length === 0 ? (
          <div className="mono c-ink-3" style={{ padding: '12px 0' }}>
            Stel een geplande oplevering-datum in via Instellingen → Project om de tijdlijn te zien.
          </div>
        ) : (
          <div className={styles.ruler}>
            <div className={styles.rulerRail} />
            <div className={`row ${styles.rulerRow}`}>
              {decadeRuler.map((r) => (
                <div key={r.label} className={`col ${styles.rulerCell}`}>
                  <div
                    className={`${styles.rulerDot} ${
                      r.now || r.start ? styles.rulerDotActive : r.end ? styles.rulerDotEnd : ''
                    }`}
                  />
                  <div
                    className={`${styles.rulerLabel} ${r.now || r.start || r.end ? styles.rulerLabelBold : ''}`}
                  >
                    {r.label}
                  </div>
                  <div className={`mono ${styles.rulerDate}`}>{r.date}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <div className={styles.body}>
        <Card className={styles.garList}>
          <div className={styles.garHead}>
            <h3>Garanties per element</h3>
            <QueryBoundary query={garanties} loading="Garanties laden…">
              {(page) => (
                <p className={styles.garSub}>{page.items.length} elementen geregistreerd</p>
              )}
            </QueryBoundary>
          </div>
          <QueryBoundary query={garanties} loading="Garanties laden…">
            {(page) =>
              page.items.length === 0 ? (
                <div className="mono c-ink-3" style={{ padding: '24px' }}>
                  Nog geen garanties geregistreerd.
                </div>
              ) : (
                page.items.map((g) => (
                  <div key={g.id} className={`row gap-12 ${styles.garRow}`}>
                    <div className="flex-1">
                      <div className={styles.garElement}>{g.component}</div>
                      <div className={styles.garElementSub}>{g.supplier}</div>
                    </div>
                    <Pill kind={pillKindFor(g)} dot={false}>
                      {durationLabel(g.startsAt, g.endsAt)}
                    </Pill>
                    <span className={`mono ${styles.garEnd}`}>tot {formatShortDate(g.endsAt)}</span>
                    <Icon name="chevR" size={13} color="var(--ink-4)" />
                  </div>
                ))
              )
            }
          </QueryBoundary>
        </Card>

        {/* Onderhoudsschema + Opleveringspunten cards removed 2026-05-17 —
            both had no API backing; the previous "HVAC SeBau / CV-ketel
            Vandeput / Voegwerk Heyvaert" rows and "Voorlopige oplevering
            42/47" counters were Phase-1 fixture data. Re-introduce when
            matching entities (MaintenanceSchedule, OpleveringsPunt) land
            on the backend. */}
      </div>
    </AppFrame>
  );
}
